var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from 'react';
import { v4 as uuid } from 'uuid';
import styled, { css } from 'styled-components';
import { deprecated } from '@ally/metronome-icons';
import { Icon } from '../Icon';
import { ErrorNotification } from '../ErrorNotification';
import { addAllyTM, get, propsToForward } from '../tools/utils';
import { gatherValues } from './utils';
import whisper from '../whisper';
import { Label } from '../Label';
const StandardOption = styled.option `
  color: initial;
  font-style: initial;
`;
const StandardOptGroup = styled.optgroup `
  color: initial;
  font-style: initial;
`;
const placeholderStyles = css `
  color: ${get.colors('gray-700')};
  font-style: italic;
`;
const Option = ({ label, value, isDisabled }) => {
    return (React.createElement(StandardOption, { value: value, disabled: isDisabled }, label));
};
const OptionGroup = ({ label, options }) => {
    return (React.createElement(StandardOptGroup, { label: label, "data-testid": label }, options.map(({ label: optionLabel, value, isDisabled, }) => (React.createElement(Option, { label: optionLabel, value: value, isDisabled: isDisabled, key: `${optionLabel}_${value}` })))));
};
const Options = ({ options }) => {
    return (React.createElement(React.Fragment, null, options.map(option => {
        if ('value' in option) {
            const { value, label, isDisabled } = option;
            return (React.createElement(Option, { value: value, label: label, isDisabled: isDisabled, key: `${label}_${value}` }));
        }
        const { label, options: optgroupOptions } = option;
        return (React.createElement(OptionGroup, { label: label, options: optgroupOptions, key: `${label}_${JSON.stringify(optgroupOptions)}` }));
    })));
};
const errorStyles = css `
  border-bottom: 1px solid ${get.colors('error')};
`;
const SelectContainer = styled.div `
  width: 100%;
  max-width: 100%;
  display: inline-block;
  text-align: center;
  position: relative;
  vertical-align: top;
`;
const SelectIcon = styled(Icon) `
  margin-top: -8px;
  right: 11px;
  position: absolute;
  display: block;
  top: 55%;
  pointer-events: none;
  path {
    fill: ${({ theme: { colors }, isDisabled }) => {
    if (isDisabled)
        return colors['slate-3'];
    return colors.bluesuedeshoes;
}}
`;
const StyledSelect = styled.select.withConfig({
    shouldForwardProp: propsToForward,
})(({ theme: { colors }, value, $error, $allValues, placeholder }) => css `
    width: 100%;
    max-width: 100%;
    appearance: none;
    box-shadow: none;
    border-radius: 0;
    padding: 4px 2.5em 4px 10px;
    background-color: ${colors['gray-100']};
    border: none;
    /*
   Adding 1px to the height to compensate for the 1px border
   The actual input area is just 44px.
  */
    height: 45px;
    border-bottom-width: 1px;
    border-bottom-style: solid;
    border-bottom-color: ${colors['periwinkle-500']};
    margin-bottom: -1px;
    font-size: 16px;
    color: ${get.colors('slate-5')};
    line-height: 24px;
    vertical-align: top;
    display: inline-flex;
    justify-content: flex-start;
    align-items: center;
    position: relative;
    cursor: pointer;
    &[disabled] {
      background-color: ${colors['slate-1']};
      cursor: not-allowed;
      color: ${colors['slate-3']};
      ::after {
        color: ${colors['slate-3']};
      }
    }
    &:focus {
      height: 47px;
      border-bottom-width: 3px;
      border-bottom-color: ${colors.bluesuedeshoes};
      margin-bottom: -3px;
      outline: none;
    }
    &::-ms-expand {
      display: none;
    }
    ${$error ? errorStyles : ''}
    ${(value === undefined || !$allValues.includes(value)) && !!placeholder
    ? placeholderStyles
    : ''}
  `);
const StyledErrorNotification = styled(ErrorNotification)(({ theme: { space } }) => css `
    margin-top: ${space.xs};
  `);
const StyledLabel = styled(Label)(({ theme: { space } }) => css `
    display: block;
    margin-bottom: ${space.xs};
  `);
const StyledDiv = styled.div `
  display: flex;
  flex-direction: column;
  width: 100%;
`;
const SingleSelect = React.forwardRef((_a, ref) => {
    var { options, id, name, label, className = '', isDisabled = false, error, placeholder, 
    /**
     * required is used to enable browser validation. If you are using this
     * component with useForm, you do not need this.
     */
    required = false, value, withAllyTM = true, removeBlankOption = false } = _a, rest = __rest(_a, ["options", "id", "name", "label", "className", "isDisabled", "error", "placeholder", "required", "value", "withAllyTM", "removeBlankOption"]);
    const { current: defaultSelectID } = React.useRef(uuid());
    const safeSelectId = id || defaultSelectID;
    const { current: labelID } = React.useRef(uuid());
    const allValues = gatherValues(options);
    const isValidValue = !value || allValues.includes(value);
    React.useEffect(() => {
        if (!isValidValue) {
            whisper.info({
                message: `${value} passed to value prop; however, ${value} does not correspond to a value in the options array (i.e., ${JSON.stringify(allValues).replaceAll(/[[\]]/g, '')}).`,
            });
        }
    }, [isValidValue, value]);
    const { current: UUID } = React.useRef(uuid());
    const allyTM = addAllyTM({
        content: value,
        id: id || `select-${UUID}`,
        elementType: 'form',
        withAllyTM,
    });
    let placeholderOption;
    if (placeholder) {
        placeholderOption = React.createElement(Option, { label: placeholder, value: "" });
    }
    else if (removeBlankOption) {
        placeholderOption = React.createElement(React.Fragment, null);
    }
    else {
        placeholderOption = React.createElement(Option, { label: "", value: "" });
    }
    return (React.createElement(StyledDiv, null,
        !!label && (React.createElement(StyledLabel, { id: labelID, htmlFor: safeSelectId }, label)),
        React.createElement(SelectContainer, { error: error, isDisabled: isDisabled, className: className },
            React.createElement(StyledSelect, Object.assign({ "data-testid": "mui-select", ref: ref, id: label ? safeSelectId : id, name: name || id, disabled: isDisabled, required: required, value: value, "$allValues": allValues, placeholder: placeholder, "$error": error }, (error && {
                'aria-describedby': `${id}Error`,
                'data-allytm-error': error,
            }), (label && {
                'aria-labelledby': labelID,
            }), allyTM, rest),
                placeholderOption,
                React.createElement(Options, { options: options })),
            React.createElement(SelectIcon, { icon: deprecated.muiChevronDown, size: "sm", isDisabled: isDisabled, ariaHidden: true })),
        error && (React.createElement(StyledErrorNotification, { id: `${id}Error`, errorMessage: error }))));
});
export { SingleSelect };
//# sourceMappingURL=SingleSelect.js.map